import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import InterviewUpgradeGate, { isUpgradeRequiredError, refusalSentence, useGateStatus } from './InterviewUpgradeGate';
import { holdsPaidExam } from '../../quiz-generation/components/LockedPartNotice';
import { ExhibitCard } from './InterviewBoard';
import RichText, { PromptLine } from './RichText';
import { openingAddsNothing, plainText } from '../promptText';
import { clearDraft, loadDraft, saveDraft, saveResult } from '../writtenStorage';
import './InterviewBrowse.css';
import './InterviewWritten.css';

/**
 * A mock interview paper taken in writing: every question in the session,
 * each answered one part at a time. Each answer is marked as it is
 * submitted, by the same judge as the live interview, so the paper adapts as
 * the interview does: a strong answer is taken further, and an answer with a
 * particular mistake gets the follow-up written for that mistake. Nothing is
 * hinted unasked; after a while on one part, the student can ask for a hint.
 *
 * The server holds every submitted answer (one run per question), so a
 * reload rebuilds the paper from the runs. This device keeps only which run
 * belongs to which question, the unsent text, and the time.
 */
export const HINT_AFTER_SECONDS = 180;
export const HINT_PREFIX = 'Hint: ';
export const SKIPPED = '(no answer)';
const WRITTEN = { mode: 'written' };
const PART_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

function exhibitOf(node) {
  if (!node?.exhibit_text) return null;
  return {
    exhibit_text: node.exhibit_text,
    exhibit_is_html: node.exhibit_is_html ?? node.exhibit_text.trim().startsWith('<'),
  };
}

/**
 * What a student sees for one thing the examiner put to them. A main
 * follow-up is shown from the paper's own markup (so formulas and emphasis
 * survive), with its exhibit; a return to it keeps the examiner's lead-in; a
 * follow-up written for this answer is shown as said. The first step's text
 * also carries the question, which is already on the page above it.
 */
export { plainText };

export function stepFromPrompt(question, content, rungId, index) {
  const rung = (question.rungs || []).find((r) => r.rung_id === rungId);
  if (rung) {
    const said = plainText(content);
    const asked = plainText(rung.prompt);
    if (asked && said.endsWith(asked)) {
      const lead = index === 0 ? '' : said.slice(0, said.length - asked.length).trim();
      return { lead, html: rung.prompt, text: null, rungId, exhibit: exhibitOf(rung), sketch: Boolean(rung.sketch) };
    }
  }
  if (index === 0) return { lead: '', html: null, text: null, rungId: null, exhibit: null, sketch: false };
  // A follow-up written for this answer: PromptLine finds its markup in the paper.
  return { lead: '', html: null, text: content, rungId, exhibit: null, sketch: false };
}

/** A follow-up's exhibit belongs where it first appears, not on every return. */
function withExhibitsOnce(steps) {
  const seen = new Set();
  return steps.map((s) => {
    if (!s.exhibit) return s;
    if (seen.has(s.rungId)) return { ...s, exhibit: null };
    seen.add(s.rungId);
    return s;
  });
}

export function firstSteps(question) {
  const first = (question.rungs || [])[0];
  if (!first) return [{ ...stepFromPrompt(question, '', null, 0), answer: null, hint: null }];
  return withExhibitsOnce([{
    ...stepFromPrompt(question, `${plainText(question.opening_prompt)} ${plainText(first.prompt)}`, first.rung_id, 0),
    answer: null,
    hint: null,
  }]);
}

/** A reloaded run's conversation, as the steps above. */
export function stepsFromTranscript(question, transcript) {
  const steps = [];
  (transcript || []).forEach((t) => {
    if (t.speaker === 'interviewer') {
      if (t.content.startsWith(HINT_PREFIX) && steps.length) {
        steps[steps.length - 1].hint = t.content.slice(HINT_PREFIX.length);
        return;
      }
      steps.push({ ...stepFromPrompt(question, t.content, t.rung_id, steps.length), answer: null, hint: null });
    } else if (steps.length) {
      steps[steps.length - 1].answer = t.content;
    }
  });
  return withExhibitsOnce(steps);
}

function firstHintAvailable(question) {
  const first = (question.rungs || [])[0];
  return Boolean(first && (first.branches || []).some((b) => b.branch_kind_id === 'rung_hint'));
}

function formatClock(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * The paper's answers are saved under the signed-in user's id, so nothing is
 * loaded until that id is known: without it the paper would open empty and
 * its autosave would overwrite the student's saved answers once the id came.
 * Keyed on the id, so a paper held in memory never carries over to another
 * account.
 */
export default function WrittenPaperPage() {
  const { user } = useAuth();
  const userId = user?.id || null;
  if (!userId) return <div className="ib"><div className="ib__state">Loading…</div></div>;
  return <WrittenPaper key={userId} userId={userId} paid={holdsPaidExam(user)} />;
}

function WrittenPaper({ userId, paid }) {
  const { workspace: workspaceSlug, leafKey, paperId, sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | error | upgrade
  const [gateCode, statusFor] = useGateStatus();
  const [paper, setPaper] = useState(null);
  const [byQuestion, setByQuestion] = useState({}); // qid -> {runId, steps, complete, hintAvailable, shownAt}
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({}); // qid -> 'marking' | 'hint'
  const [errors, setErrors] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState('');
  const elapsedRef = useRef(0);

  const questions = useMemo(() => (paper?.questions || [])
    .filter((q) => String(q.session_id) === String(sessionId))
    .sort((a, b) => (a.question_number || 0) - (b.question_number || 0)), [paper, sessionId]);
  const session = useMemo(
    () => (paper?.sessions || []).find((s) => String(s.session_id) === String(sessionId)),
    [paper, sessionId],
  );

  // ---- load the paper, then rebuild any questions already started ----------
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setStatus('loading');
      let data;
      try {
        data = await interviewRuntimeAPI.getPaper(paperId);
      } catch (err) {
        if (!cancelled) setStatus(statusFor(err));
        return;
      }
      const draft = loadDraft(userId, paperId, sessionId) || {};
      const qs = (data.questions || []).filter((q) => String(q.session_id) === String(sessionId));
      const now = Date.now();
      const entries = await Promise.all(qs.map(async (q) => {
        const qid = q.external_question_id;
        const runId = draft.runs?.[qid];
        const fresh = { runId: null, steps: firstSteps(q), complete: false, hintAvailable: firstHintAvailable(q), shownAt: now };
        if (!runId) return [qid, fresh];
        try {
          const run = await interviewRuntimeAPI.getRun(runId);
          const steps = stepsFromTranscript(q, run.transcript);
          return [qid, {
            runId, steps: steps.length ? steps : fresh.steps, complete: Boolean(run.is_complete),
            hintAvailable: Boolean(run.hint_available), shownAt: now,
          }];
        } catch {
          return [qid, fresh]; // a run this account can't read any more: start the question afresh
        }
      }));
      if (cancelled) return;
      elapsedRef.current = draft.elapsed || 0;
      setElapsed(elapsedRef.current);
      setDrafts(draft.drafts || {});
      setByQuestion(Object.fromEntries(entries));
      setPaper(data);
      setStatus('ready');
    }
    boot();
    return () => { cancelled = true; };
  }, [userId, paperId, sessionId]);

  // ---- keep this device's part in step --------------------------------------
  useEffect(() => {
    if (status !== 'ready') return;
    const runs = Object.fromEntries(Object.entries(byQuestion).map(([qid, s]) => [qid, s.runId]).filter(([, r]) => r));
    saveDraft(userId, paperId, sessionId, { runs, drafts, elapsed: elapsedRef.current });
  }, [byQuestion, drafts, status, userId, paperId, sessionId]);

  useEffect(() => {
    if (status !== 'ready' || finishing) return undefined;
    const tick = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      if (elapsedRef.current % 15 === 0) {
        const draft = loadDraft(userId, paperId, sessionId) || {};
        saveDraft(userId, paperId, sessionId, { ...draft, elapsed: elapsedRef.current });
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [status, finishing, userId, paperId, sessionId]);

  // One interview's time: an Oxbridge paper's stated time shared across its
  // interviews (30 minutes each; Oxford PPE's three, 20). A Medicine or
  // Dentistry paper runs its school's time per station, which the course
  // page hands over, or which is looked up on the course when the paper is
  // opened directly. Untimed where the school's timing gives none.
  const isSchool = ['Medicine', 'Dentistry'].includes(paper?.metadata?.subject_name);
  const [schoolMinutes, setSchoolMinutes] = useState(location.state?.minutes ?? null);
  useEffect(() => {
    if (!isSchool || schoolMinutes !== null || !interviewRuntimeAPI.getLeafOverview) return undefined;
    let cancelled = false;
    interviewRuntimeAPI.getLeafOverview(leafKey)
      .then((overview) => {
        const found = (overview.papers || []).flatMap((p) => p.interviews).find((s) => s.session_id === sessionId);
        if (!cancelled) setSchoolMinutes(found?.minutes || 0);
      })
      .catch(() => { if (!cancelled) setSchoolMinutes(0); });
    return () => { cancelled = true; };
  }, [isSchool, schoolMinutes, leafKey, sessionId]);
  const targetSeconds = useMemo(() => {
    if (!paper) return 0;
    if (isSchool) return schoolMinutes ? schoolMinutes * 60 : 0;
    const sessions = (paper.sessions || []).length || 1;
    return Math.round(((paper.metadata.duration_minutes || 60) / sessions) * 60);
  }, [paper, isSchool, schoolMinutes]);

  const patch = useCallback((qid, change) => {
    setByQuestion((prev) => ({ ...prev, [qid]: { ...prev[qid], ...change } }));
  }, []);

  const ensureRun = useCallback(async (q) => {
    const qid = q.external_question_id;
    const existing = byQuestion[qid]?.runId;
    if (existing) return existing;
    const run = await interviewRuntimeAPI.startRun(qid, WRITTEN);
    patch(qid, { runId: run.run_id });
    return run.run_id;
  }, [byQuestion, patch]);

  const submitPart = useCallback(async (q, override) => {
    const qid = q.external_question_id;
    const text = (override ?? drafts[qid] ?? '').trim();
    if (!text || busy[qid]) return;
    setBusy((b) => ({ ...b, [qid]: 'marking' }));
    setErrors((e) => ({ ...e, [qid]: '' }));
    try {
      const runId = await ensureRun(q);
      const turn = await interviewRuntimeAPI.submitTurn(runId, text, WRITTEN);
      setByQuestion((prev) => {
        const cur = prev[qid];
        const steps = cur.steps.map((s, i) => (i === cur.steps.length - 1 ? { ...s, answer: text } : s));
        if (!turn.is_complete && turn.say) {
          steps.push({ ...stepFromPrompt(q, turn.say, turn.rung_id, steps.length), answer: null, hint: null });
        }
        return {
          ...prev,
          [qid]: {
            ...cur, runId, steps: withExhibitsOnce(steps), complete: Boolean(turn.is_complete),
            hintAvailable: Boolean(turn.hint_available), shownAt: Date.now(),
          },
        };
      });
      setDrafts((d) => ({ ...d, [qid]: '' }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [qid]: isUpgradeRequiredError(err)
          ? refusalSentence(err, 'Mock interview papers are on Beyond.')
          : 'That answer could not be marked just now. It is still in the box; please try again.',
      }));
    } finally {
      setBusy((b) => ({ ...b, [qid]: null }));
    }
  }, [drafts, busy, ensureRun]);

  const skipPart = useCallback((q) => {
    if (window.confirm('Move on without answering this part? A part left blank is marked "Needs work".')) {
      submitPart(q, SKIPPED);
    }
  }, [submitPart]);

  const askHint = useCallback(async (q) => {
    const qid = q.external_question_id;
    if (busy[qid]) return;
    setBusy((b) => ({ ...b, [qid]: 'hint' }));
    try {
      const runId = await ensureRun(q);
      const { hint } = await interviewRuntimeAPI.takeHint(runId);
      setByQuestion((prev) => {
        const cur = prev[qid];
        const steps = cur.steps.map((s, i) => (i === cur.steps.length - 1 ? { ...s, hint } : s));
        return { ...prev, [qid]: { ...cur, runId, steps, hintAvailable: false } };
      });
    } catch {
      patch(qid, { hintAvailable: false });
    } finally {
      setBusy((b) => ({ ...b, [qid]: null }));
    }
  }, [busy, ensureRun, patch]);

  const finish = async () => {
    const started = questions.filter((q) => byQuestion[q.external_question_id]?.runId);
    if (!started.length || finishing) return;
    const unstarted = questions.length - started.length;
    const unfinished = started.filter((q) => !byQuestion[q.external_question_id].complete);
    const warnings = [];
    if (unstarted) {
      warnings.push(unstarted === 1
        ? 'One question has no answer, so it will not be marked.'
        : `${unstarted} questions have no answer, so they will not be marked.`);
    }
    if (unfinished.length) {
      warnings.push(unfinished.length === 1
        ? 'One question is not finished; the parts you have not reached will not be marked.'
        : `${unfinished.length} questions are not finished; the parts you have not reached will not be marked.`);
    }
    if (warnings.length && !window.confirm(`${warnings.join(' ')}\n\nFinish your paper now?`)) return;

    setFinishing(true);
    setFinishError('');
    try {
      await Promise.all(unfinished.map((q) => interviewRuntimeAPI.endRun(byQuestion[q.external_question_id].runId)));
      const runIds = started.map((q) => byQuestion[q.external_question_id].runId);
      saveResult(userId, paperId, sessionId, runIds);
      clearDraft(userId, paperId, sessionId);
      navigate(`/interviews/${workspaceSlug}/${leafKey}/${paperId}/feedback`, { state: { runIds, mode: 'written' } });
    } catch {
      setFinishError('Your paper could not be finished just now. Your answers are saved; please try again.');
      setFinishing(false);
    }
  };

  const briefUrl = `/interviews/${workspaceSlug}/${leafKey}/${paperId}`;

  if (status === 'loading') return <div className="ib"><div className="ib__state">Loading…</div></div>;
  if (status === 'upgrade') return <div className="ib"><InterviewUpgradeGate code={gateCode} paid={paid} /></div>;
  if (status === 'error' || !paper || !questions.length) {
    return (
      <div className="ib">
        <div className="ib__state ib__state--error">
          Could not load this paper. <Link to={briefUrl}>Back</Link>
        </div>
      </div>
    );
  }
  if (finishing) {
    return (
      <div className="ib">
        <div className="iw-marking" role="status">
          <h1 className="ib__title">Finishing your paper…</h1>
        </div>
      </div>
    );
  }

  const { metadata } = paper;
  const over = targetSeconds && elapsed > targetSeconds;
  const anyStarted = questions.some((q) => byQuestion[q.external_question_id]?.runId);
  const now = Date.now();

  return (
    <div className="ib iw">
      <div className="ib__crumbs">
        <Link to="/apps">Apps</Link> / <Link to={`/interviews/${workspaceSlug}/${leafKey}`}>{metadata.institution}</Link> / <Link to={briefUrl}>{metadata.title}</Link>
      </div>
      <h1 className="ib__title">{metadata.title}</h1>
      <p className="ib__lede">
        Written paper{session?.title ? ` · ${session.title}` : ''} · {questions.length} question{questions.length === 1 ? '' : 's'}{targetSeconds ? ` · about ${Math.round(targetSeconds / 60)} minutes` : ''}
      </p>

      <div className="iw-note">
        Answer each part in writing, as fully as you would aloud. Each answer is marked when you submit it, and
        the next part depends on your answer, as it would with an examiner. If you are stuck on a part for a
        while, you can ask for a hint. Your answers are saved as you go.
      </div>

      {targetSeconds > 0 && (
        <div className={`iw-clock${over ? ' iw-clock--over' : ''}`} aria-live="off">
          <span>{formatClock(elapsed)}</span>
          <span className="iw-clock-target">/ {formatClock(targetSeconds)}</span>
          {over && <span className="iw-clock-msg">Past the suggested time. Finish when you are ready.</span>}
        </div>
      )}

      {questions.map((q, qi) => {
        const qid = q.external_question_id;
        const state = byQuestion[qid];
        if (!state) return null;
        const openingOnly = !(q.rungs || []).length;
        const current = state.complete ? -1 : state.steps.length - 1;
        const waited = (now - state.shownAt) / 1000;
        const canHint = !state.complete && state.hintAvailable && waited >= HINT_AFTER_SECONDS && !busy[qid];
        const questionExhibit = exhibitOf(q);
        return (
          <section className="iw-q" key={qid} aria-labelledby={`iw-q-${qi}`}>
            <h2 className="iw-q-title" id={`iw-q-${qi}`}>Question {qi + 1}</h2>
            {!openingAddsNothing(q.opening_prompt, (q.rungs || [])[0]?.prompt) && (
              <RichText html={q.opening_prompt} className="iw-prompt" />
            )}
            {questionExhibit && <ExhibitCard exhibit={questionExhibit} kicker="Exhibit" />}

            {state.steps.map((step, i) => {
              const label = openingOnly ? null : `(${PART_LETTERS[i] || i + 1})`;
              const isCurrent = i === current;
              const answered = step.answer != null;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div className={`iw-part${answered ? ' iw-part--locked' : ''}`} key={i}>
                  {(step.lead || step.html || step.text || label) && (
                    <div className="iw-part-head">
                      {label && <span className="iw-part-label">{label}</span>}
                      <div>
                        {step.lead && <p className="iw-lead">{step.lead}</p>}
                        {step.html && <RichText html={step.html} className="iw-prompt" />}
                        {step.text && <PromptLine question={q} said={step.text} className="iw-prompt" />}
                      </div>
                    </div>
                  )}
                  {step.exhibit && <ExhibitCard exhibit={step.exhibit} kicker="Exhibit" />}
                  {step.sketch && isCurrent && (
                    <p className="iw-hint">Describe in words what you would draw, and what it shows.</p>
                  )}
                  {step.hint && (
                    <div className="iw-hint-box"><span className="iw-hint-label">Hint</span> {step.hint}</div>
                  )}
                  {answered && (
                    <div className={`ir-answer${step.answer === SKIPPED ? ' ir-answer--empty' : ''}`}>
                      {step.answer === SKIPPED ? 'No answer given.' : step.answer}
                    </div>
                  )}
                  {isCurrent && (
                    <>
                      <label className="iw-sr" htmlFor={`iw-a-${qid}`}>
                        {`Your answer to question ${qi + 1}${label ? ` part ${label}` : ''}`}
                      </label>
                      <textarea
                        id={`iw-a-${qid}`}
                        className="iw-answer"
                        rows={6}
                        value={drafts[qid] || ''}
                        disabled={busy[qid] === 'marking'}
                        onChange={(e) => setDrafts((d) => ({ ...d, [qid]: e.target.value }))}
                        placeholder="Type your answer"
                      />
                      {errors[qid] && <div className="ib__state--error iw-submit-error">{errors[qid]}</div>}
                      <div className="iw-actions">
                        <button
                          type="button"
                          className="ib__btn"
                          disabled={!(drafts[qid] || '').trim() || Boolean(busy[qid])}
                          onClick={() => submitPart(q)}
                        >
                          {busy[qid] === 'marking' ? 'Marking…' : 'Submit answer'}
                        </button>
                        {canHint && (
                          <button type="button" className="ib__btn ib__btn--ghost" onClick={() => askHint(q)}>
                            Need a hint?
                          </button>
                        )}
                        <button type="button" className="ib__link-btn" disabled={Boolean(busy[qid])} onClick={() => skipPart(q)}>
                          Skip this part
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            {state.complete && <p className="iw-done">Question complete.</p>}
          </section>
        );
      })}

      <div className="iw-submit">
        {finishError && <div className="ib__state--error iw-submit-error">{finishError}</div>}
        <button type="button" className="ib__btn" disabled={!anyStarted} onClick={finish}>
          Finish paper
        </button>
        <p className="iw-submit-note">You will see your marks and feedback, then a review of model answers.</p>
      </div>
    </div>
  );
}
